import { localeCode, formatDate } from "../../services/localeFormat"
import React, {useEffect, useState, useMemo} from "react"
import {Spin, Empty, Button, notification, Modal, Select, message} from "antd"
import {HandHeart, Repeat, CalendarCheck, Receipt, Sparkles, TrendingUp, Clock, CreditCard, FileText, FileBadge, Download, ChevronDown} from "lucide-react"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {AccountAPI, recuUrl, cerfaUrl} from "./api"
import { isEmbedded, navigateOut } from "../../services/embedHost"

function formatMoney(value, devise, lang) {
    const code = (devise || "eur").toUpperCase()
    try {
        return new Intl.NumberFormat(localeCode(lang), {style: "currency", currency: code, maximumFractionDigits: 0}).format(value || 0)
    } catch (_) {
        return (value || 0) + " " + code
    }
}

function tint(hex, alpha) {
    if (typeof hex !== "string" || !hex.startsWith("#")) return "rgba(43,112,169,0.08)"
    return hex + alpha
}

// Onglet courant : `window.open` après un `await` (hors geste utilisateur direct) est
// bloqué par les bloqueurs de popups, Safari iOS en tête — le clic ne faisait rien.
// Le tunnel de paiement ramène de toute façon sur le site via son returnUrl.
function openUrl(url) {
    if (typeof window !== "undefined") window.location.assign(url)
}

/**
 * Ouvrir un lien de RÈGLEMENT (distinct d'un reçu, qu'on continue d'ouvrir dans
 * un onglet). Le lien vient d'être créé par le serveur, donc le geste de
 * l'utilisateur a expiré : en mode intégré, on demande au site hôte de naviguer
 * plutôt que de tenter une sortie que le navigateur refuserait en silence — et
 * la page de paiement s'ouvre en pleine fenêtre, jamais dans l'iframe.
 */
function openPaiementUrl(url) {
    if (isEmbedded()) {
        navigateOut(url)
        return
    }
    openUrl(url)
}

export default function Dashboard({account, primary = "#2B70A9", lang = "fr"}) {
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState({summary: null, promesses: [], abonnements: [], inscriptions: [], paiements: []})
    const [tab, setTab] = useState("paiements")
    const [payingId, setPayingId] = useState(null)         // id de la promesse/occasion en cours
    const [exportOpen, setExportOpen] = useState(false)

    const prenom = account?.profile?.prenom || null

    useEffect(() => {
        let mounted = true
        ;(async () => {
            try {
                const res = await AccountAPI.dashboard()
                if (mounted && res?.success) {
                    setData({
                        summary: res.summary || null,
                        promesses: res.promesses || [],
                        abonnements: res.abonnements || [],
                        inscriptions: res.inscriptions || [],
                        paiements: res.paiements || [],
                    })
                }
            } finally {
                if (mounted) setLoading(false)
            }
        })()
        return () => { mounted = false }
    }, [])

    const devise = useMemo(() => {
        const all = [...(data.promesses || []), ...(data.paiements || [])]
        return all.find(i => i.devise)?.devise || "eur"
    }, [data])

    // Régler UNE promesse.
    async function payPromesse(p) {
        setPayingId(p.id)
        try {
            const res = await AccountAPI.payLinkPromesse(p.id)
            if (res?.success && res.url) {
                openPaiementUrl(res.url)
            } else {
                notification.error({message: res?.error === "ALREADY_PAID" ? t`Cette promesse est déjà réglée.` : t`Impossible de générer le lien de paiement.`})
            }
        } finally {
            setPayingId(null)
        }
    }

    // Régler un groupe (occasion donnée, ou tout pour un contact/asso).
    async function payGroup({contactId, assoc,devise, occasion, key}) {
        setPayingId(key)
        try {
            const res = await AccountAPI.payLinkGroup({contactId, assoc,devise, occasion})
            if (res?.success && res.url) {
                openPaiementUrl(res.url)
            } else {
                notification.error({message: res?.error === "NOTHING_DUE" ? t`Rien à régler.` : t`Impossible de générer le lien de paiement.`})
            }
        } finally {
            setPayingId(null)
        }
    }

    if (loading) {
        return <div style={{textAlign: "center", padding: "60px 0"}}><Spin size="large"/></div>
    }

    const {summary, promesses, abonnements, inscriptions, paiements} = data

    return (
        <div>
            <Greeting prenom={prenom} primary={primary}/>

            <SummaryStrip summary={summary} devise={devise} primary={primary} lang={lang}/>

            {/* Barre d'onglets icône + label court : TIENT toujours sur ~360px (4 items),
                donc PAS de scroll horizontal (et donc pas de problème d'auto-scroll).
                C'est le pattern d'app mobile standard pour une nav à onglets fixe. */}
            <TabBar
                primary={primary}
                value={tab}
                onChange={setTab}
                tabs={[
                    {value: "paiements", label: t`Derniers`, icon: <Receipt size={19}/>, count: paiements.length},
                    {value: "promesses", label: t`Promesses`, icon: <HandHeart size={19}/>, count: promesses.length},
                    {value: "abonnements", label: t`Mensuels`, icon: <Repeat size={19}/>, count: abonnements.length},
                    {value: "inscriptions", label: t`Inscrits`, icon: <CalendarCheck size={19}/>, count: inscriptions.length},
                ]}
            />

            {tab === "promesses" && (
                <PromessesList
                    items={promesses} devise={devise} primary={primary} lang={lang}
                    summary={summary}
                    onPay={payPromesse} onPayOccasion={payGroup}
                    payingId={payingId}
                />
            )}
            {tab === "abonnements" && <AbonnementsList items={abonnements} lang={lang}/>}
            {tab === "inscriptions" && <InscriptionsList items={inscriptions} primary={primary} lang={lang}/>}
            {tab === "paiements" && (
                <PaiementsList
                    items={paiements} primary={primary} lang={lang}
                    onExport={() => setExportOpen(true)}
                />
            )}

            <ExportModal open={exportOpen} onClose={() => setExportOpen(false)} primary={primary}/>
        </div>
    )
}

// Barre d'onglets responsive (icône + label court). 4 items en flex:1 → tient sur
// mobile sans scroll. Item actif = fond teinté + couleur primary. Compteur en badge.
function TabBar({tabs, value, onChange, primary}) {
    return (
        <div
            className="acs-tabbar"
            style={{
                display: "flex", gap: 6, marginTop: 20, marginBottom: 20,
                background: "#fff", border: "1px solid #f0ece3", borderRadius: 14,
                padding: 5, boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            }}
        >
            {tabs.map(tb => {
                const active = tb.value === value
                return (
                    <button
                        key={tb.value}
                        onClick={() => onChange(tb.value)}
                        className="acs-tabbar-item"
                        style={{
                            flex: 1, minWidth: 0, border: "none", cursor: "pointer",
                            background: active ? tint(primary, "16") : "transparent",
                            color: active ? primary : "#8a8278",
                            borderRadius: 10, padding: "8px 4px",
                            display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                            transition: "background .15s, color .15s",
                        }}
                    >
                        <span style={{position: "relative", display: "inline-flex"}}>
                            {tb.icon}
                            {tb.count > 0 && (
                                <span style={{
                                    position: "absolute", top: -6, right: -10,
                                    minWidth: 15, height: 15, padding: "0 4px", boxSizing: "border-box",
                                    background: active ? primary : "#c9c2b4", color: "#fff",
                                    borderRadius: 999, fontSize: 9.5, fontWeight: 700, lineHeight: "15px",
                                    textAlign: "center",
                                }}>{tb.count > 99 ? "99+" : tb.count}</span>
                            )}
                        </span>
                        <span className="acs-tabbar-label" style={{
                            fontSize: 12, fontWeight: active ? 600 : 500,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
                        }}>{tb.label}</span>
                    </button>
                )
            })}
        </div>
    )
}

function Greeting({prenom, primary}) {
    return (
        <div className="acs-greeting" style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 18, fontSize: 20, fontWeight: 700}}>
            <Sparkles size={22} color={primary}/>
            <div>
                {prenom ? <Trans>Bonjour {prenom}</Trans> : <Trans>Bonjour</Trans>}
            </div>
        </div>
    )
}

function SummaryStrip({summary, devise, primary, lang}) {
    if (!summary) return null
    // Les KPI sont scopés à la DEVISE PRINCIPALE (summary.devise, calculée backend) :
    // montants en devise étrangère ignorés → total incomplet mais jamais faux.
    const kpiDevise = summary.devise || devise
    const tiles = []
    if (summary.totalReste > 0) tiles.push({key: "reste", icon: <Clock size={20}/>, color: "#E08D2F", label: t`Reste à régler`, value: formatMoney(summary.totalReste, kpiDevise, lang)})
    if (summary.totalDonne > 0) tiles.push({key: "donne", icon: <TrendingUp size={20}/>, color: "#1E9E6A", label: summary.year ? t`Donné en ${summary.year}` : t`Déjà donné`, value: formatMoney(summary.totalDonne, kpiDevise, lang)})
    const counts = summary.counts || {}
    if (counts.promesses > 0) tiles.push({key: "cp", icon: <HandHeart size={20}/>, color: primary, label: t`Promesses actives`, value: counts.promesses})
    if (counts.abonnements > 0) tiles.push({key: "ca", icon: <Repeat size={20}/>, color: "#7C5CD6", label: t`Dons mensuels`, value: counts.abonnements})
    if (tiles.length === 0) return null

    return (
        <div style={{marginBottom: 8}}>
            <div className="acs-stats-grid" style={{display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(min(140px, 100%), 1fr))`, gap: 12}}>
                {tiles.map(tl => (
                    <div key={tl.key} className="acs-stat" style={{background: "#fff", border: "1px solid #f0ece3", borderRadius: 16, padding: "16px 18px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
                        <div className="acs-stat-icon" style={{width: 38, height: 38, borderRadius: 10, background: tint(tl.color, "1A"), color: tl.color, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10}}>{tl.icon}</div>
                        <div className="acs-stat-value" style={{fontSize: 22, fontWeight: 700, lineHeight: 1.1}}>{tl.value}</div>
                        <div className="acs-stat-label" style={{fontSize: 12.5, color: "#9b9488", marginTop: 2}}>{tl.label}</div>
                    </div>
                ))}
            </div>
        </div>
    )
}

function ItemCard({icon, accent, title, subtitle, member, right, footer}) {
    return (
        <div className="acs-card" style={{
            display: "flex", alignItems: "center", gap: 14,
            background: "#fff", border: "1px solid #f0ece3", borderLeft: `4px solid ${accent}`,
            borderRadius: 14, padding: "14px 16px", marginBottom: 10, boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
            flexWrap: "wrap",
        }}>
            <div className="acs-card-icon" style={{flexShrink: 0, width: 42, height: 42, borderRadius: 11, background: tint(accent, "14"), color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center"}}>{icon}</div>
            <div style={{minWidth: 0, flex: 1}}>
                <div className="acs-card-title" style={{fontWeight: 600, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{title}</div>
                <div style={{display: "flex", gap: 8, alignItems: "center", marginTop: 3, flexWrap: "wrap"}}>
                    {member && <span className="acs-card-sub" style={{fontSize: 12, color: "#a8a195"}}>{member}</span>}
                    {subtitle && <span className="acs-card-sub" style={{fontSize: 12, color: "#a8a195"}}>{subtitle}</span>}
                </div>
            </div>
            {right && <div className="acs-card-right" style={{flexShrink: 0, textAlign: "right"}}>{right}</div>}
            {footer && <div className="acs-card-footer" style={{flexBasis: "100%", marginTop: 10}}>{footer}</div>}
        </div>
    )
}

function Pill({children, color, bg}) {
    return <span style={{display: "inline-block", fontSize: 12, fontWeight: 600, color, background: bg, borderRadius: 999, padding: "3px 10px"}}>{children}</span>
}

function EmptyState({text}) {
    return <Empty description={text} style={{padding: "40px 0"}}/>
}

// Une carte de promesse (réutilisée dans chaque groupe d'occasion).
// Une promesse n'est payable par le donateur que si elle est active, non soldée et
// non couverte par un prélèvement en cours (l'API renvoie `active` et
// `hasActiveAbonnement` ; ils étaient ignorés → bouton « Régler » vers un lien expiré).
function isPayablePromesse(p) {
    if(p.status){
        return p.status === "OPEN" || p.status === "PARTIAL"
    }
    return p.active !== false && !p.hasActiveAbonnement && (p.reste || 0) > 0
}

function PromesseCard({p, devise, onPay, payingId, lang}) {
    const canceled = p.status === "CANCELED" || p.active === false
    const covered = p.status === "COVERED" || !!p.hasActiveAbonnement
    const settled = p.status === "FULFILLED" || (!covered && (p.reste || 0) <= 0)
    const payable = isPayablePromesse(p)

    let accent = "#E08D2F"
    let pill = <Pill color="#C9781E" bg="#FCEFD9">{t`Reste`} {formatMoney(p.reste, p.devise || devise, lang)}</Pill>
    if (canceled) {
        accent = "#9B9488"
        pill = <Pill color="#6F6A62" bg="#EDEBE7"><Trans>Annulée</Trans></Pill>
    } else if (covered) {
        accent = "#5B5FB8"
        pill = <Pill color="#4C50A8" bg="#E9E9F8"><Trans>Couverte par un prélèvement</Trans></Pill>
    } else if (settled) {
        accent = "#1E9E6A"
        pill = <Pill color="#1E9E6A" bg="#E6F4EC"><Trans>Réglée</Trans></Pill>
    }

    return (
        <ItemCard
            accent={accent}
            icon={<HandHeart size={20}/>}
            title={p.name || t`Promesse`}
            subtitle={p.cerfa===false ? t`Part sans reçu fiscal` : null}
            member={p.contactName}
            right={
                <div>
                    <div className="acs-card-amount" style={{fontWeight: 700, fontSize: 15}}>{formatMoney(p.montant, p.devise || devise, lang)}</div>
                    <div style={{marginTop: 4}}>
                        {pill}
                    </div>
                </div>
            }
            footer={payable && (
                <Button
                    type="primary" ghost block icon={<CreditCard size={15}/>}
                    loading={payingId === p.id}
                    onClick={() => onPay(p)}
                    style={{borderRadius: 10, fontWeight: 600}}
                >
                    <Trans>Régler cette promesse</Trans>
                </Button>
            )}
        />
    )
}

function PromessesList({items, devise, primary, summary, onPay, onPayOccasion, payingId, lang}) {
    if (!items.length) return <EmptyState text={t`Aucune promesse de don`}/>

    // Groupe par occasion (en conservant l'ordre d'apparition). Les promesses sans
    // occasion vont dans un groupe "Autres promesses".
    const groups = []          // [{key, occasion, assoc, contactId, items[], reste, dueCount}]
    const byKey = {}
    const OTHER = "__other__"
    for (const p of items) {
        const occ = p.occasion || null
        const key = [p.contactId || "self",p.assoc || "MAIN",p.devise || devise,occ || OTHER].join("|")
        if (!byKey[key]) {
            byKey[key] = {key, occasion: occ, assoc: p.assoc,devise:p.devise || devise,contactId: p.contactId, items: [], reste: 0, dueCount: 0}
            groups.push(byKey[key])
        }
        byKey[key].items.push(p)
        if (isPayablePromesse(p)) { byKey[key].reste += p.reste; byKey[key].dueCount++ }
    }

    // Règlement « tout » groupé par (contact, association) : un lien de paiement
    // = un compte d'encaissement = une asso = une devise. On ne peut donc PAS
    // régler en un seul lien des promesses d'assos/devises différentes. On propose
    // un bouton par groupe (contact+asso), avec le total DANS la devise de ce groupe.
    const payGroups = []
    const payByKey = {}
    for (const p of items) {
        if (!isPayablePromesse(p)) continue
        const gAssoc = p.assoc || "MAIN"
        const gContact = p.contactId || null
        const gDevise=p.devise || devise
        const gkey = gContact + "|" + gAssoc + "|" + gDevise
        if (!payByKey[gkey]) {
            payByKey[gkey] = {key: gkey, assoc: gAssoc, contactId: gContact, contactName: p.contactName || null, devise: gDevise, reste: 0}
            payGroups.push(payByKey[gkey])
        }
        payByKey[gkey].reste += p.reste
    }
    // Plusieurs personnes liées au compte → on précise le nom sur le bouton.
    const multiContacts = new Set(payGroups.map(g => g.contactId)).size > 1

    return (
        <div>
            {/* Régler mes promesses — un bouton par (contact, association/devise). */}
            {payGroups.map(g => (
                <Button
                    key={g.key}
                    type="primary" block icon={<CreditCard size={15}/>}
                    loading={payingId === ("grp:" + g.key)}
                    onClick={() => onPayOccasion({contactId: g.contactId, assoc: g.assoc,devise:g.devise, key: "grp:" + g.key})}
                    style={{marginBottom: 10, height: 38, borderRadius: 10, fontWeight: 600, fontSize: 14}}
                >
                    {multiContacts && g.contactName
                        ? <Trans>Régler les promesses de {g.contactName} ({formatMoney(g.reste, g.devise, lang)})</Trans>
                        : <Trans>Régler mes promesses ({formatMoney(g.reste, g.devise, lang)})</Trans>}
                </Button>
            ))}
            {payGroups.length > 0 && <div style={{height: 6}}/>}

            {groups.map(g => (
                <div key={g.key} style={{marginBottom: 18}}>
                    {/* Header d'occasion */}
                    {g.occasion ? (
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                            padding: "4px 2px 10px", flexWrap: "wrap",
                        }}>
                            <div style={{minWidth: 0}}>
                                <div style={{fontWeight: 700, fontSize: 15}}>{g.occasion}</div>
                                {g.reste > 0 && (
                                    <div style={{fontSize: 12, color: "#9b9488"}}>{t`Reste`} {formatMoney(g.reste, g.items[0]?.devise || devise, lang)}</div>
                                )}
                            </div>
                            {g.dueCount > 1 && (
                                <Button
                                    type="primary" size="small" icon={<CreditCard size={15}/>}
                                    loading={payingId === ("occ:" + g.key)}
                                    onClick={() => onPayOccasion({contactId: g.contactId, assoc: g.assoc,devise:g.devise, occasion: g.occasion, key: "occ:" + g.key})}
                                    style={{borderRadius: 8}}
                                >
                                    <Trans>Tout régler</Trans>
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div style={{fontWeight: 700, fontSize: 15, padding: "4px 2px 10px", color: "#8a8278"}}>
                            <Trans>Autres promesses</Trans>
                        </div>
                    )}

                    {/* Promesses de l'occasion */}
                    {g.items.map(p => (
                        <PromesseCard key={p.id} p={p} devise={devise} onPay={onPay} payingId={payingId} lang={lang}/>
                    ))}
                </div>
            ))}
        </div>
    )
}

function AbonnementsList({items, lang}) {
    if (!items.length) return <EmptyState text={t`Aucun don mensuel`}/>
    return items.map(a => (
        <ItemCard
            key={a.id} accent="#7C5CD6" icon={<Repeat size={20}/>}
            title={t`Don mensuel`}
            subtitle={a.next ? (t`Prochain` + " " + formatDate(a.next, lang, "DD/MM/YYYY")) : null}
            member={a.contactName}
            right={
                <div>
                    <div className="acs-card-amount" style={{fontWeight: 700, fontSize: 15}}>{formatMoney((a.amount || 0) / 100, a.devise, lang)}</div>
                    <div style={{marginTop: 4}}>
                        <SubscriptionStatus subscription={a}/>
                    </div>
                </div>
            }
        />
    ))
}

// The API owns lifecycle and bank processing. A bank debit in transit can
// coexist with a paused or finished contract and never replaces that state.
function SubscriptionStatus({subscription: a}) {
    let label, color="#6F6A62", bg="#EDEBE7"
    if (["FINISHED","CANCELED","CANCELLED"].includes(a.state) || a.state==="ACTIVE" && a.nextPaymentStatus==="FINISHED") {
        label=t`Terminé`
    } else if (a.state==="PAUSE" || !a.state && a.nextPaymentStatus==="PAUSED") {
        label=t`En pause`
    } else if (a.state==="CARD_EXPIRED") {
        label=t`Carte expirée`;color="#C9781E";bg="#FCEFD9"
    } else if (["MANUAL","FAILED","ERROR"].includes(a.state)) {
        label=t`À vérifier`;color="#C9781E";bg="#FCEFD9"
    } else if (!a.active || a.state==="INIT") {
        label=t`En attente`
    } else if (a.state==="ACTIVE" || !a.state) {
        label=t({message:"Activé",context:"subscription.status",comment:"État actif du contrat, distinct du traitement bancaire d’une échéance."})
        color="#1E9E6A";bg="#E6F4EC"
    } else {
        label=t`À vérifier`;color="#C9781E";bg="#FCEFD9"
    }
    return <div style={{display:"grid",justifyItems:"end",textAlign:"end",gap:4,maxInlineSize:"min(14rem, 42vw)",overflowWrap:"anywhere"}}>
        <Pill color={color} bg={bg}>{label}</Pill>
        {a.nextPaymentStatus==="PROCESSING" && <Pill color="#4C50A8" bg="#E9E9F8">{t`Prélèvement en cours`}</Pill>}
    </div>
}

// Pagination "Voir plus" par paquets de PAGE_SIZE (pattern mobile). Réinitialise
// quand la liste change (changement d'onglet/refresh).
const PAGE_SIZE = 20
function Paginated({items, render}) {
    const [count, setCount] = useState(PAGE_SIZE)
    const shown = items.slice(0, count)
    const remaining = items.length - shown.length
    return (
        <div>
            {shown.map(render)}
            {remaining > 0 && (
                <Button
                    block
                    onClick={() => setCount(c => c + PAGE_SIZE)}
                    style={{marginTop: 6, borderRadius: 10, fontWeight: 600}}
                >
                    <Trans>Voir plus ({remaining})</Trans>
                </Button>
            )}
        </div>
    )
}

// Résumé d'une soumission (mêmes données que l'email : [{title, items:[{name,value}]}]).
// Affiché dans un panneau repliable sous la carte d'inscription.
function SummaryBlock({summary, primary}) {
    const [open, setOpen] = useState(false)
    const sections = Array.isArray(summary) ? summary.filter(s => s && Array.isArray(s.items) && s.items.length) : []
    if (!sections.length) return null
    return (
        <div style={{flexBasis: "100%", marginTop: 10}}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    border: "none", background: "transparent", cursor: "pointer", padding: 0,
                    color: primary, fontWeight: 600, fontSize: 13,
                    display: "inline-flex", alignItems: "center", gap: 5,
                }}
            >
                <ChevronDown size={15} style={{transform: open ? "rotate(180deg)" : "none", transition: "transform .15s"}}/>
                {open ? <Trans>Masquer le détail</Trans> : <Trans>Voir le détail</Trans>}
            </button>
            {open && (
                <div style={{marginTop: 8, background: "#faf8f4", border: "1px solid #f0ece3", borderRadius: 10, padding: "10px 12px"}}>
                    {sections.map((sec, si) => (
                        <div key={si} style={{marginBottom: si < sections.length - 1 ? 10 : 0}}>
                            {sec.title && <div style={{fontWeight: 700, fontSize: 12.5, color: "#8a8278", marginBottom: 4}}>{sec.title}</div>}
                            {sec.items.map((it, ii) => (
                                <div key={ii} style={{display: "flex", justifyContent: "space-between", gap: 12, padding: "3px 0", fontSize: 13}}>
                                    <span style={{color: "#9b9488", flexShrink: 0}}>{it.name}</span>
                                    <span style={{textAlign: "right", fontWeight: 500, wordBreak: "break-word"}}>{summaryValue(it.value)}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// La valeur peut être une string (mode plain) ou, par sécurité, un objet {__html}.
function summaryValue(v) {
    if (v && typeof v === "object" && typeof v.__html === "string") {
        return <span dangerouslySetInnerHTML={{__html: v.__html}}/>
    }
    return v == null ? "" : String(v)
}

function InscriptionsList({items, primary, lang}) {
    if (!items.length) return <EmptyState text={t`Aucune inscription`}/>
    return (
        <Paginated
            items={items}
            render={(i) => (
                <ItemCard
                    key={i.id} accent={primary} icon={<CalendarCheck size={20}/>}
                    title={i.label || t`Inscription`}
                    subtitle={i.datetime ? formatDate(i.datetime, lang, "DD MMM YYYY") : null}
                    member={i.contactName}
                    right={i.state === "VALIDE"
                        ? <Pill color="#1E9E6A" bg="#E6F4EC"><Trans>Validée</Trans></Pill>
                        : <Pill color="#8a8278" bg="#f0ece3"><Trans>En attente</Trans></Pill>}
                    footer={<SummaryBlock summary={i.summary} primary={primary}/>}
                />
            )}
        />
    )
}

function PaiementsList({items, primary, onExport, lang}) {
    if (!items.length) return <EmptyState text={t`Aucun paiement`}/>
    return (
        <div>
            <div style={{display: "flex", justifyContent: "flex-end", marginBottom: 12}}>
                <Button icon={<Download size={16}/>} onClick={onExport} style={{borderRadius: 10}}>
                    <Trans>Exporter mes reçus fiscaux</Trans>
                </Button>
            </div>
            <Paginated
                items={items}
                render={(p) => (
                    <ItemCard
                        key={p.id} accent="#1E9E6A" icon={<Receipt size={20}/>}
                        title={p.name || t`Paiement`}
                        subtitle={<>
                            {p.datetime ? formatDate(p.datetime, lang, "DD MMM YYYY") : null}
                            {Number.isSafeInteger(p.fiscalAmount) && <>
                                <br/><Trans>Part avec reçu fiscal</Trans> : {formatMoney(p.fiscalAmount/100,p.devise,lang)}
                                <br/><Trans>Part sans reçu fiscal</Trans> : {formatMoney((p.amount-p.fiscalAmount)/100,p.devise,lang)}
                            </>}
                            {p.receiptAllocationException && (p.cerfaId || p.recuId) && <><br/><Trans>Le reçu existant est conservé. Ce rattachement ne modifie pas le traitement fiscal de ce paiement.</Trans></>}
                        </>}
                        member={p.contactName}
                        right={<div className="acs-card-amount" style={{fontWeight: 700, fontSize: 15}}>{formatMoney((p.amount || 0) / 100, p.devise, lang)}</div>}
                        footer={(p.recuId || p.cerfaId) && (
                            <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
                                {p.recuId && (
                                    <Button size="small" icon={<FileText size={14}/>} onClick={() => openUrl(recuUrl(p.recuId))} style={{borderRadius: 8}}>
                                        <Trans>Reçu</Trans>
                                    </Button>
                                )}
                                {p.cerfaId && (
                                    <Button size="small" icon={<FileBadge size={14}/>} onClick={() => openUrl(cerfaUrl(p.cerfaId))} style={{borderRadius: 8}}>
                                        <Trans>Reçu fiscal</Trans>
                                    </Button>
                                )}
                            </div>
                        )}
                    />
                )}
            />
        </div>
    )
}

function ExportModal({open, onClose, primary}) {
    const [years, setYears] = useState([])
    const [year, setYear] = useState(null)
    const [loading, setLoading] = useState(false)
    const [sending, setSending] = useState(false)

    useEffect(() => {
        if (!open) return
        setLoading(true)
        ;(async () => {
            try {
                const res = await AccountAPI.receiptYears()
                const ys = res?.years || []
                setYears(ys)
                setYear(ys[0] || null)
            } finally {
                setLoading(false)
            }
        })()
    }, [open])

    async function doExport() {
        if (!year) return
        setSending(true)
        try {
            const res = await AccountAPI.exportReceipts(year)
            if (res?.success) {
                message.success(t`Vos reçus vous ont été envoyés par email.`)
                onClose()
            } else {
                notification.error({message: t`Aucun reçu fiscal pour cette année.`})
            }
        } finally {
            setSending(false)
        }
    }

    return (
        <Modal open={open} onCancel={onClose} footer={null} destroyOnHidden title={t`Exporter mes reçus fiscaux`}>
            {loading ? (
                <div style={{textAlign: "center", padding: 24}}><Spin/></div>
            ) : years.length === 0 ? (
                <Empty description={t`Aucun reçu fiscal disponible`}/>
            ) : (
                <div>
                    <p style={{color: "#777", marginBottom: 12}}>
                        <Trans>Vos reçus fiscaux de l'année choisie vous seront envoyés par email dans une archive.</Trans>
                    </p>
                    <Select
                        value={year} onChange={setYear} style={{width: "100%", marginBottom: 16}}
                        options={years.map(y => ({value: y, label: String(y)}))}
                    />
                    <Button type="primary" block loading={sending} icon={<Download size={16}/>} onClick={doExport} style={{borderRadius: 10, fontWeight: 600}}>
                        <Trans>Recevoir mes reçus par email</Trans>
                    </Button>
                </div>
            )}
        </Modal>
    )
}
